<template>
    <div class="paypal-container">
        <PuSkeleton height="50px" v-if="loading" :count="2"></PuSkeleton>
        <div ref="paypal-buttons" class="paypal-checkout-button" v-show="isShowPayPalButton"></div>
    </div>

</template>

<script>
import PaypalApi from '@support/paypalApi';
import paymentMethodMixin from '@mixins/paymentMethodMixin';
import oneClickPaymentMethod from '@mixins/oneClickPaymentMethod';
import braintree from 'braintree-web';
import CheckoutApi from '@support/checkoutApi';

export default {
    name: 'brainTreeOneClickPayPalCheckout',
    mixins: [oneClickPaymentMethod, paymentMethodMixin],
    data: () => ({
        loading: false,
        payPalInstance: null,
        payPalButtonRendered: false,
        deviceData: null,
        payPalPaymentData: null,
        scriptsLoaded: false,
    }),
    computed: {
        token() {
            return this.paymentMethod.public.token || null;
        },
        environment() {
            return this.paymentMethod.public.environment || 'production';
        },
        payPalAccount() {
            return this.paymentMethod.public.paypal_account || null;
        },
        isShowPayPalButton() {
            return this.payPalButtonRendered && !this.disabled;
        },
        payPalClientId() {
            return this.paymentMethod.public.paypal_client_id || null;
        },
        payPalMerchantId() {
            return this.paymentMethod.public.paypal_merchant_id || null;
        },
        payPalSdkSettings() {
            const params = {
                components: 'buttons,messages',
                commit: true,
                'disable-funding': 'venmo,card',
            };

            if (this.payPalClientId) {
                params['client-id'] = this.payPalClientId;
            }

            if (this.payPalMerchantId) {
                params['merchant-id'] = this.payPalMerchantId;
            }

            return params;
        },
        payPalButtonsSettings() {
            return {
                params: {
                    flow: 'checkout',
                    currency: 'USD',
                    enableShippingAddress: true,
                    shippingAddressEditable: true,
                },
                style: {
                    size: 'responsive',
                    shape: 'rect',
                },
            };
        },
    },
    mounted() {
        this.initForm();
    },
    methods: {
        initForm() {
            if (this.loading) {
                return;
            }

            this.loading = true;
            this.payPalInstance = null;
            this.payPalPaymentData = null;
            this.payPalButtonRendered = false;
            this.$refs['paypal-buttons'].innerHTML = '';

            braintree.client.create({authorization: this.token}).
                then(clientInstance => this.createPayPalButton(clientInstance));
        },
        createPayPalButton(clientInstance) {
            console.debug('createPayPalButton');

            braintree.dataCollector.create({
                client: clientInstance,
            }, (err, dataCollectorInstance) => {
                if (err) {
                    console.error('Error creating PayPal Checkout:', err);
                    this.handlePaymentIsNotSupported();
                    return;
                }
                this.deviceData = dataCollectorInstance.deviceData;
            });

            braintree.paypalCheckout.create({
                client: clientInstance,
            }).then(paypalCheckoutInstance => {
                return paypalCheckoutInstance.loadPayPalSDK(this.payPalSdkSettings);
            }).then(this.renderPayPalButtons).then(() => {
                this.payPalButtonRendered = true;
                this.loading = false;
            }).catch(e => {
                this.payPalButtonRendered = false;
                this.loading = false;
                console.error('Error creating PayPal Checkout:', e);
                this.handlePaymentIsNotSupported();
            });
        },
        renderPayPalButtons(paypalCheckoutInstance) {
            this.payPalInstance = paypalCheckoutInstance;

            let requests = [];

            const {style, params} = this.payPalButtonsSettings;

            const onCreateOrderCallback = () => {
                console.debug('[PayPalCheckout] paymentCallback');
                return PaypalApi.setupCheckout().then(checkout => {
                    return this.payPalInstance.createPayment(PaypalApi.getPaymentObject(checkout, params));
                });
            };

            const onShippingChangeCallback = (data, actions) => {
                console.debug('[PayPalCheckout] onShippingChangeCallback');
                return PaypalApi.onShippingChange(data, actions);
            };

            const onApproveCallback = (data, actions) => {
                console.debug('[PayPalCheckout] onApproveCallback');
                const loading = this.$loading({
                    lock: true,
                });
                this.payPalInstance.tokenizePayment(data).then(payload => {
                    console.debug(payload, data, actions);
                    return PaypalApi.normalizeCheckoutRequest(data, actions, payload);
                }).then(form => {
                    return CheckoutApi.submitPayment(form);
                }).then((response) => {
                    const {status, message} = CheckoutApi.handlePaymentResponse(response);

                    if (status !== 'success') {
                        this.$notify({
                            type: status || 'error',
                            message: message,
                            duration: 10000,
                        });
                    }
                }).catch(error => {
                    this.showPaymentErrorMessage();
                    console.error(error);
                }).finally(() => {
                    loading.close();
                });
            };

            const onClickCallback = (data, actions) => {
                console.debug('[PayPalCheckout] onClickCallback');
                PaypalApi.setInstance(this);
            };

            const onCancelCallback = () => {
                console.debug('[PayPalCheckout] onCancelCallback');
                this.showPaymentCanceledMessage();
            };

            const onErrorCallback = (err) => {
                console.debug('[PayPalCheckout] onErrorCallback');
                this.showPaymentErrorMessage();
                console.error(err);
            };

            const button = paypal.Buttons({
                commit: true,
                style,
                createOrder: onCreateOrderCallback,
                onShippingChange: onShippingChangeCallback,
                onApprove: onApproveCallback,
                onClick: onClickCallback,
                onCancel: onCancelCallback,
                onError: onErrorCallback,
            });

            requests.push(button.render(this.$refs['paypal-buttons']));

            return Promise.all(requests).then(() => {
                this.payPalButtonRendered = true;
                this.loading = false;
            }).catch(() => {
                this.handlePaymentIsNotSupported();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.paypal-container {
    width: 100%;

    h4 {
        display: block;
        width: 100%;
        text-align: center;
    }

    @media screen and (max-width: 576px) {
        padding: 2rem 0;

        .paypal-checkout-button {
            width: 100%;
        }
    }

    @media screen and (min-width: 576px) {
        padding: 2rem 0;

        .paypal-checkout-button {
            width: 400px;
            margin: 0 auto;
            padding-bottom: 10px;
        }
    }
}
</style>
